import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Chip, Grid, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, Tooltip,
  LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FactCheckIcon from '@mui/icons-material/FactCheck';

import { useApi } from '../../../hooks/useApi';
import { countRows, daysUntil, relativeDayPhrase, phaseLabel, phaseColor, rowStaleness, rowWaitDays } from '../qbrUtils';
import AuditFindingBlade from '../../AuditCenter/AuditFindingBlade';

const AUDIT_STATUS    = ['not started', 'in progress', 'completed'];
const MEETING_STATUS  = ['not scheduled', 'scheduled', 'held'];
const QBR_STATUS      = ['upcoming', 'open', 'completed'];

function statusColor(status) {
  switch (status) {
    case 'completed': return 'success';
    case 'in progress':
    case 'scheduled':
    case 'open':
    case 'held':
      return 'info';
    default: return 'default';
  }
}

// Deterministic QBR audit run id — must match the string minted by
// cma/qbr/Start-QbrCycleAudits.ps1 ("QBR-<account>-<qbr_cycle>").
function qbrAuditRunId(cycle) {
  if (!cycle?.account || !cycle?.qbr_cycle) return null;
  return `QBR-${cycle.account}-${cycle.qbr_cycle}`;
}

function answerColor(answer) {
  switch ((answer || '').toUpperCase()) {
    case 'PASS':    return 'success';
    case 'WARNING': return 'warning';
    case 'FAIL':
    case 'ERROR':   return 'error';
    default:        return 'default';   // PENDING / unknown
  }
}

function Detail() {
  const { id } = useParams();
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [cycle, setCycle] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [auditResults, setAuditResults] = useState([]);
  const [bladeIdx, setBladeIdx] = useState(null); // index into auditResults; null = closed
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Local edit state
  const [prepNotes, setPrepNotes] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  // Separate local state for the meeting-date field so the "did it change?"
  // check in onBlur compares the typed value against the original — not
  // against itself (which is what happened when we wrote both to cycle.meeting_date).
  const [meetingDateDraft, setMeetingDateDraft] = useState('');

  // Append-row dialogs
  // 'audit_issues' | 'action_items' -> cycle PATCH append
  // 'opportunities'                 -> POST /api/cma/qbropportunity (separate container)
  const [appendOpen, setAppendOpen] = useState(null);
  const [appendRow, setAppendRow] = useState({});

  const loadCycle = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const [cResp, oResp] = await Promise.all([
        jApi.request('GET', `cma/qbrcycle?id=${encodeURIComponent(id)}`),
        jApi.request('GET', `cma/qbropportunity?qbrcycle_id=${encodeURIComponent(id)}`),
      ]);
      const list = Array.isArray(cResp.data) ? cResp.data : [];
      const c = list[0] || null;
      setCycle(c);
      setPrepNotes(c?.prep_notes || '');
      setMeetingNotes(c?.meeting_notes || '');
      setMeetingDateDraft(c?.meeting_date || '');
      setOpportunities(Array.isArray(oResp.data) ? oResp.data : []);

      // Automated audit run. The run id is deterministic
      // (QBR-<account>-<qbr_cycle>) — same string the Start-QbrCycleAudits
      // VES script mints. Records live in the shared cma.audit container.
      const runId = qbrAuditRunId(c);
      if (runId) {
        try {
          const aResp = await jApi.request(
            'GET', `cma/audit?auditRunId=${encodeURIComponent(runId)}`);
          setAuditResults(Array.isArray(aResp.data) ? aResp.data : []);
        } catch {
          setAuditResults([]);
        }
      } else {
        setAuditResults([]);
      }
    } catch (ex) {
      setErrorMessage(ex?.toString?.() || 'Failed to load cycle');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadCycle(); /* eslint-disable-next-line */ }, [id]);

  const patchCycle = async (patch, logSummary) => {
    try {
      setSaving(true);
      const body = { id, ...patch };
      const resp = await jApi.request('PATCH', 'cma/qbrcycle', body);
      setCycle(resp.data);
      if (logSummary) setToast({ severity: 'success', text: logSummary });
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Update failed';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (field, value) => patchCycle({ [field]: value }, 'Status updated');
  const handleMeetingDateBlur = () => {
    if (!cycle || !meetingDateDraft) return;
    if (meetingDateDraft === cycle.meeting_date) return;   // unchanged
    patchCycle({ meeting_date: meetingDateDraft }, 'Meeting date updated');
  };
  const handleSaveNotes = () => patchCycle({
    prep_notes: prepNotes,
    meeting_notes: meetingNotes,
  }, 'Notes saved');

  const openAppend = (kind) => { setAppendOpen(kind); setAppendRow({}); };
  const closeAppend = () => { setAppendOpen(null); setAppendRow({}); };
  const submitAppend = async () => {
    if (!appendOpen) return;
    if (appendOpen === 'opportunities') {
      // Separate container — POST instead of cycle PATCH
      try {
        setSaving(true);
        const body = {
          qbrprogram_id:            cycle.qbrprogram_id,
          originating_qbrcycle_id:  cycle.id,
          description:              appendRow.description,
          opportunity:              appendRow.opportunity || undefined,
          stage:                    appendRow.stage || undefined,
          status:                   appendRow.status || 'open',
        };
        const resp = await jApi.request('POST', 'cma/qbropportunity', body);
        setOpportunities([resp.data, ...opportunities]);
        setToast({ severity: 'success', text: 'Opportunity added' });
        closeAppend();
      } catch (ex) {
        const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Failed to add opportunity';
        setToast({ severity: 'error', text: msg });
      } finally {
        setSaving(false);
      }
      return;
    }
    const append = {};
    append[appendOpen] = appendRow;
    await patchCycle({ append }, `Appended to ${appendOpen.replace('_', ' ')}`);
    closeAppend();
  };

  const handleCreateAuditTicket = async () => {
    try {
      setSaving(true);
      const resp = await jApi.request('POST', 'cma/qbrcycle/audit-ticket', { id });
      setToast({
        severity: resp.data.created ? 'success' : 'info',
        text: resp.data.created
          ? `Created audit ticket ${resp.data.audit_ticket}`
          : `Audit ticket already exists: ${resp.data.audit_ticket}`,
      });
      setCycle(resp.data.cycle);
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Audit ticket creation failed';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleRunAudits = async () => {
    try {
      setSaving(true);
      // Queue the VES script — it owns the whole orchestration (ticket +
      // automated audit set). Idempotent, so re-clicking is safe.
      const job = await jApi.request('POST', 'ves/job', {
        functionpath: 'cma/qbr/Start-QbrCycleAudits.ps1',
        splat: { CycleId: id },
      });
      const jobId = job?.data?.id || '(queued)';
      setToast({ severity: 'success', text: `QBR audits queued (ves job ${jobId})` });
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Audit run enqueue failed';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      setSaving(true);
      const job = await jApi.request('POST', 'ves/job', {
        functionpath: 'cma/qbr/Refresh-QbrCycleStatus.ps1',
        splat: { CycleId: id },
      });
      const jobId = job?.data?.id || '(queued)';
      setToast({ severity: 'success', text: `Refresh queued (ves job ${jobId})` });
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Refresh enqueue failed';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleRoll = async () => {
    try {
      setSaving(true);
      const resp = await jApi.request('POST', 'cma/qbrcycle/roll', { id });
      const next = resp.data.next;
      setToast({
        severity: resp.data.rolled ? 'success' : 'info',
        text: resp.data.rolled
          ? `Rolled forward to ${next?.qbr_cycle} (meeting ${next?.meeting_date})`
          : `${next?.qbr_cycle} already exists — back-link restored`,
      });
      // Reload to pick up the new next_cycle_id
      loadCycle();
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Roll failed';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/qbr" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>QBR</Typography>
              </Link>
              {cycle?.qbrprogram_id && (<>
                <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
                <Link to={`/qbr/program/${cycle.qbrprogram_id}`} style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>{cycle?.customer}</Typography>
                </Link>
              </>)}
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{cycle?.qbr_cycle || id}</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {cycle ? `${cycle.customer} — ${cycle.qbr_cycle}` : (isLoading ? 'Loading…' : 'Not found')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  {cycle && (
                    <Chip size="small"
                      label={phaseLabel(cycle.phase)}
                      color={phaseColor(cycle.phase)}/>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {cycle ? `Meeting ${cycle.meeting_date}` : ''}
                  </Typography>
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Refresh statuses from Autotask (queues a VES job)">
                  <span><Button size="small" variant="outlined" startIcon={<AutorenewIcon/>}
                    onClick={handleRefreshStatus} disabled={!cycle || saving}>Refresh status</Button></span>
                </Tooltip>
                <Tooltip title="Create the parent QBR audit ticket in Autotask">
                  <span><Button size="small" variant="outlined" startIcon={<ConfirmationNumberIcon/>}
                    onClick={handleCreateAuditTicket} disabled={!cycle || saving || !!cycle?.audit_ticket}>
                    {cycle?.audit_ticket ? 'Audit ticket exists' : 'Create audit ticket'}
                  </Button></span>
                </Tooltip>
                <Tooltip title="Queue the QBR audit run (ticket + automated audit set) as a VES job — idempotent, also auto-fires at the audit notification date">
                  <span><Button size="small" variant="outlined" startIcon={<FactCheckIcon/>}
                    onClick={handleRunAudits} disabled={!cycle || saving}>
                    Run QBR audits
                  </Button></span>
                </Tooltip>
                <Tooltip title="Roll forward to the next cycle">
                  <span><Button size="small" variant="outlined"
                    onClick={handleRoll} disabled={!cycle || saving || !!cycle?.next_cycle_id}>
                    {cycle?.next_cycle_id ? 'Already rolled' : 'Roll forward'}
                  </Button></span>
                </Tooltip>
                <IconButton size="small" onClick={loadCycle} disabled={isLoading}><RefreshIcon/></IconButton>
              </Stack>
            </Stack>
            {(isLoading || saving) && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {errorMessage && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">{errorMessage}</Typography>
          </Paper>
        )}

        {!isLoading && !cycle && !errorMessage && (
          <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">No QBR cycle found for this id.</Typography>
          </Paper>
        )}

        {cycle && (
          <>
            {/* Pre-meeting summary — at-a-glance "should I worry going into
                the meeting?" view. Shows audit-issue counts and days-to-
                meeting; flags red if open issues remain near or past the
                meeting date. */}
            {(() => {
              const issues  = countRows(cycle.audit_issues);
              const actions = countRows(cycle.action_items);
              const days    = daysUntil(cycle.meeting_date);
              // A meeting counts as held only when explicitly marked so — a
              // past meeting_date does not imply the meeting happened (it may
              // have been missed or postponed).
              const meetingHeld = cycle.meeting_status === 'held';
              const issuesRisk = issues.open > 0 && (days === null || days <= 14);

              return (
                <Paper sx={{ p: 2, mt: 2,
                    background: issuesRisk
                      ? alpha(theme.palette.warning.main, 0.08)
                      : undefined,
                    border: issuesRisk
                      ? `1px solid ${alpha(theme.palette.warning.main, 0.4)}`
                      : undefined,
                  }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
                    {meetingHeld ? 'Cycle summary' : 'Pre-meeting summary'}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="overline" color="text.secondary">Audit issues</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={`${issues.total} found`} variant="outlined"/>
                        <Chip size="small" label={`${issues.closed} closed`} color="success" variant="outlined"/>
                        <Chip size="small"
                          label={`${issues.open} open`}
                          color={issues.open > 0 ? 'warning' : 'default'}
                          variant={issues.open > 0 ? 'filled' : 'outlined'}/>
                      </Stack>
                    </Grid>
                    {meetingHeld && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="overline" color="text.secondary">Action items</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={`${actions.total} total`} variant="outlined"/>
                          <Chip size="small" label={`${actions.closed} closed`} color="success" variant="outlined"/>
                          <Chip size="small"
                            label={`${actions.open} open`}
                            color={actions.open > 0 ? 'warning' : 'default'}
                            variant={actions.open > 0 ? 'filled' : 'outlined'}/>
                        </Stack>
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="overline" color="text.secondary">Meeting</Typography>
                      <Typography variant="body1" sx={{
                        fontWeight: 600,
                        color: (days !== null && days < 0 && !meetingHeld) ? 'error.main' : 'inherit',
                      }}>
                        {cycle.meeting_date || '—'}
                        {cycle.meeting_date && (
                          <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                            ({relativeDayPhrase(cycle.meeting_date)})
                          </Typography>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  {issuesRisk && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main', fontWeight: 600 }}>
                      ⚠ {issues.open} audit issue{issues.open === 1 ? '' : 's'} still open
                      {days !== null && days >= 0 && days <= 14 && ` — meeting in ${days} day${days === 1 ? '' : 's'}`}
                      {days !== null && days <  0 && ` — meeting already passed`}
                    </Typography>
                  )}
                </Paper>
              );
            })()}

            {/* Status + dates */}
            <Paper sx={{ p: 2, mt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Audit status</InputLabel>
                    <Select label="Audit status" value={cycle.audit_status || 'not started'}
                      onChange={e => handleStatusChange('audit_status', e.target.value)}>
                      {AUDIT_STATUS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Meeting status</InputLabel>
                    <Select label="Meeting status" value={cycle.meeting_status || 'not scheduled'}
                      onChange={e => handleStatusChange('meeting_status', e.target.value)}>
                      {MEETING_STATUS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>QBR status</InputLabel>
                    <Select label="QBR status" value={cycle.qbr_status || 'upcoming'}
                      onChange={e => handleStatusChange('qbr_status', e.target.value)}>
                      {QBR_STATUS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth size="small" label="Audit ticket"
                    value={cycle.audit_ticket || ''}
                    onChange={e => setCycle({ ...cycle, audit_ticket: e.target.value })}
                    onBlur={() => patchCycle({ audit_ticket: cycle.audit_ticket }, 'Audit ticket updated')}/>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField fullWidth size="small" type="date" label="Meeting date"
                    InputLabelProps={{ shrink: true }}
                    value={meetingDateDraft}
                    onChange={e => setMeetingDateDraft(e.target.value)}
                    onBlur={handleMeetingDateBlur}
                    helperText="Changing this re-derives the notification dates"/>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="overline" color="text.secondary">Audit notify</Typography>
                  <Typography variant="body1">{cycle.audit_notification_date || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="overline" color="text.secondary">Meeting notify</Typography>
                  <Typography variant="body1">{cycle.meeting_notification_date || '—'}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Notes */}
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
                Notes
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField label="Prep notes" multiline minRows={2}
                  value={prepNotes} onChange={e => setPrepNotes(e.target.value)}/>
                <TextField label="Meeting notes" multiline minRows={3}
                  value={meetingNotes} onChange={e => setMeetingNotes(e.target.value)}/>
                <Stack direction="row" justifyContent="flex-end">
                  <Button startIcon={<SaveIcon/>} variant="contained" size="small"
                    onClick={handleSaveNotes} disabled={saving}>Save notes</Button>
                </Stack>
              </Stack>
            </Paper>

            {/* Automated audit run — records from the shared cma.audit
                container, keyed by the deterministic run id
                (QBR-<account>-<qbr_cycle>) the VES script mints. Runs in
                parallel with the Autotask audit ticket during the migration.
                Shown once the VES script has created records. */}
            {auditResults.length > 0 && (
              <Paper sx={{ p: 2, mt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="h6">Automated audit run</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {qbrAuditRunId(cycle)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {['PASS', 'WARNING', 'FAIL', 'PENDING', 'ERROR'].map(a => {
                      const n = auditResults.filter(r => (r.answer || '').toUpperCase() === a).length;
                      return n > 0 ? (
                        <Chip key={a} size="small" label={`${n} ${a.toLowerCase()}`} color={answerColor(a)}/>
                      ) : null;
                    })}
                  </Stack>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Audit</TableCell>
                        <TableCell>Section</TableCell>
                        <TableCell>Result</TableCell>
                        <TableCell>AI</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditResults.map((r, i) => (
                        <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                          onClick={() => setBladeIdx(i)}>
                          <TableCell>
                            <Typography variant="body2"
                              sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                              {r.title || r.id}
                            </Typography>
                          </TableCell>
                          <TableCell>{r.section}</TableCell>
                          <TableCell>
                            <Chip size="small" label={r.answer || 'PENDING'}
                              color={answerColor(r.answer)}/>
                          </TableCell>
                          <TableCell>
                            {r.ai && r.ai.remediationType
                              ? <Chip size="small" variant="outlined" label={r.ai.remediationType}/>
                              : <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Audit issues */}
            <EmbeddedTable
              title="Audit issues"
              rows={cycle.audit_issues || []}
              columns={[
                { key: 'description', label: 'Description' },
                { key: 'ticket',      label: 'Ticket #' },
                { key: 'status',      label: 'Status' },
              ]}
              onAdd={() => openAppend('audit_issues')}
              theme={theme}
              showAge
              fallbackDate={cycle.audit_notification_date || cycle.meeting_date}
            />

            {/* Action items */}
            <EmbeddedTable
              title="Action items"
              rows={cycle.action_items || []}
              columns={[
                { key: 'description', label: 'Description' },
                { key: 'ticket',      label: 'Ticket #' },
                { key: 'status',      label: 'Status' },
              ]}
              onAdd={() => openAppend('action_items')}
              theme={theme}
              showAge
              fallbackDate={cycle.meeting_date}
            />

            {/* Opportunities — sourced from the qbropportunity container.
                Opportunities outlive cycles, so they live in their own
                container and are referenced back via originating_qbrcycle_id. */}
            <EmbeddedTable
              title={`Opportunities from this cycle${opportunities.length ? ` (${opportunities.length})` : ''}`}
              rows={opportunities}
              columns={[
                { key: 'description', label: 'Description' },
                { key: 'opportunity', label: 'Opportunity #' },
                { key: 'stage',       label: 'Stage' },
                { key: 'status',      label: 'Status' },
                { key: 'closed_date', label: 'Closed' },
              ]}
              onAdd={() => openAppend('opportunities')}
              theme={theme}
            />

            <Box sx={{ pb: 4 }}/>
          </>
        )}
      </Container>

      {/* Audit finding slide-out — same blade as the Audit Center
          findings page. Opens on a row click in the audit-run panel. */}
      <AuditFindingBlade
        open={bladeIdx !== null}
        auditId={bladeIdx !== null ? auditResults[bladeIdx]?.id : null}
        onClose={() => setBladeIdx(null)}
        onPrev={bladeIdx > 0 ? () => setBladeIdx(bladeIdx - 1) : undefined}
        onNext={bladeIdx !== null && bladeIdx < auditResults.length - 1
          ? () => setBladeIdx(bladeIdx + 1) : undefined}
        indexLabel={bladeIdx !== null ? `${bladeIdx + 1}/${auditResults.length}` : ''}
        onChanged={loadCycle}
      />

      {/* Append-row dialog */}
      <Dialog open={!!appendOpen} onClose={closeAppend} maxWidth="sm" fullWidth>
        <DialogTitle>Add to {appendOpen?.replace('_', ' ')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Description" autoFocus
              value={appendRow.description || ''}
              onChange={e => setAppendRow({ ...appendRow, description: e.target.value })}/>
            {appendOpen !== 'opportunities' && (
              <TextField label="Ticket #"
                value={appendRow.ticket || ''}
                onChange={e => setAppendRow({ ...appendRow, ticket: e.target.value })}/>
            )}
            {appendOpen === 'opportunities' && (
              <>
                <TextField label="Opportunity #"
                  value={appendRow.opportunity || ''}
                  onChange={e => setAppendRow({ ...appendRow, opportunity: e.target.value })}/>
                <TextField label="Stage"
                  value={appendRow.stage || ''}
                  onChange={e => setAppendRow({ ...appendRow, stage: e.target.value })}/>
              </>
            )}
            <TextField label="Status"
              value={appendRow.status || ''}
              onChange={e => setAppendRow({ ...appendRow, status: e.target.value })}/>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAppend} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submitAppend} disabled={saving || !appendRow.description}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.text}</Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

function EmbeddedTable({ title, rows, columns, onAdd, theme, showAge, fallbackDate }) {
  // Age priority: row.last_activity_date (set by Refresh-QbrCycleStatus from
  // Autotask) > row.meeting_date > fallbackDate (the parent cycle's meeting
  // date). The fallback is what makes Age work on the Cycle Detail tables —
  // embedded rows there don't carry meeting_date themselves.
  const ageFor = (r) => {
    if (!showAge) return { stale: false, days: 0 };
    const merged = { ...r, meeting_date: r.meeting_date || fallbackDate };
    return { stale: rowStaleness(merged).stale, days: rowWaitDays(merged) };
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
          {title}
        </Typography>
        <Button size="small" startIcon={<AddIcon/>} onClick={onAdd}>Add</Button>
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(c => <TableCell key={c.key}>{c.label}</TableCell>)}
              {showAge && <TableCell align="right" sx={{ width: 90 }}>Age</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + (showAge ? 1 : 0)} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                  None.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r, idx) => {
              const { stale, days } = ageFor(r);
              return (
                <TableRow key={idx} hover>
                  {columns.map(c => (
                    <TableCell key={c.key}>{r[c.key] || '—'}</TableCell>
                  ))}
                  {showAge && (
                    <TableCell align="right">
                      {days > 0
                        ? <Chip size="small"
                            label={`${days}d`}
                            color={stale ? 'error' : 'default'}
                            variant={stale ? 'filled' : 'outlined'}/>
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Detail;
